/**
 * Estimated completion time for a questionnaire, from the number of questions of each type.
 *
 * Two costs add up for every question: *reading* it and *answering* it. Reading time is
 * words ÷ reading speed; the default speed is 238 words per minute, Brysbaert's (2019)
 * meta-analytic mean for silent reading of English non-fiction (190 studies, 18,573
 * participants). Answering time is a per-type allowance in seconds. The allowances are
 * planning defaults, not measured constants: response times vary with the item's
 * complexity and the respondent's age and experience (Yan & Tourangeau, 2008), which is why
 * every figure here is adjustable and why a pilot test is the real answer. The estimate is
 * for a typical respondent; a second figure multiplies it for a slower one.
 */

export const QUESTION_TYPES = [
  "yesNo",
  "single",
  "multi",
  "likert",
  "grid",
  "number",
  "ranking",
  "shortText",
  "openText",
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

export interface TypeAssumption {
  /** Seconds to decide and respond, excluding reading. */
  seconds: number;
  /** Words a respondent reads for one question of this type: stem plus options. */
  words: number;
}

/** Planning defaults. Adjustable in the tool; calibrate them with a pilot. */
export const DEFAULT_ASSUMPTIONS: Record<QuestionType, TypeAssumption> = {
  yesNo: { seconds: 3, words: 12 },
  single: { seconds: 5, words: 30 },
  multi: { seconds: 9, words: 35 },
  likert: { seconds: 5, words: 18 },
  grid: { seconds: 4, words: 8 },
  number: { seconds: 6, words: 12 },
  ranking: { seconds: 25, words: 45 },
  shortText: { seconds: 15, words: 15 },
  openText: { seconds: 60, words: 20 },
};

export const DEFAULT_READING_SPEED = 238;
export const DEFAULT_SLOW_FACTOR = 1.5;

export interface LengthInput {
  counts: Record<QuestionType, number>;
  assumptions: Record<QuestionType, TypeAssumption>;
  /** Words of consent, instructions and thank-you text a respondent reads in all. */
  introWords: number;
  /** Words per minute for silent reading. */
  readingSpeed: number;
  /** Multiplier for a slower respondent. */
  slowFactor: number;
}

export interface TypeTime {
  type: QuestionType;
  count: number;
  readSeconds: number;
  answerSeconds: number;
  totalSeconds: number;
}

export interface LengthResult {
  rows: TypeTime[];
  introSeconds: number;
  questionCount: number;
  totalSeconds: number;
  slowSeconds: number;
  /** The whole minutes to announce: the estimate rounded up. */
  announceMinutes: number;
}

export function readingSeconds(words: number, speed: number): number {
  return speed > 0 ? (words / speed) * 60 : 0;
}

export function estimateLength(input: LengthInput): LengthResult {
  const rows = QUESTION_TYPES.map((type): TypeTime => {
    const count = Math.max(0, input.counts[type]);
    const { seconds, words } = input.assumptions[type];
    const readSeconds = count * readingSeconds(words, input.readingSpeed);
    const answerSeconds = count * seconds;
    return {
      type,
      count,
      readSeconds,
      answerSeconds,
      totalSeconds: readSeconds + answerSeconds,
    };
  });
  const introSeconds = readingSeconds(input.introWords, input.readingSpeed);
  const totalSeconds =
    introSeconds + rows.reduce((sum, row) => sum + row.totalSeconds, 0);
  return {
    rows,
    introSeconds,
    questionCount: rows.reduce((sum, row) => sum + row.count, 0),
    totalSeconds,
    slowSeconds: totalSeconds * input.slowFactor,
    announceMinutes: Math.ceil(totalSeconds / 60 - 1e-9),
  };
}

/** 754 → "12:34". Whole seconds, rounded. */
export function minutesAndSeconds(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${(whole % 60).toString().padStart(2, "0")}`;
}
